import { storableError } from '../../util/errors';
import { 
  getArtists, 
  isArtistUsernamesFetchInProgress, 
  getArtistUsernamesError,
  fetchArtistUsernames,
  fetchArtistsWithListings
} from '../../ducks/artistUsernames.duck';

// ================ Action types ================ //

export const FETCH_ARTISTS_REQUEST = 'app/ArtistDirectoryPage/FETCH_ARTISTS_REQUEST';
export const FETCH_ARTISTS_SUCCESS = 'app/ArtistDirectoryPage/FETCH_ARTISTS_SUCCESS';
export const FETCH_ARTISTS_ERROR = 'app/ArtistDirectoryPage/FETCH_ARTISTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  artists: [],
  fetchArtistsError: null,
  fetchArtistsInProgress: false,
};

export default function artistDirectoryPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ARTISTS_REQUEST:
      return {
        ...state,
        fetchArtistsInProgress: true,
        fetchArtistsError: null,
      };
    case FETCH_ARTISTS_SUCCESS:
      return {
        ...state,
        artists: payload,
        fetchArtistsInProgress: false,
      };
    case FETCH_ARTISTS_ERROR:
      return {
        ...state,
        fetchArtistsError: payload,
        fetchArtistsInProgress: false,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchArtistsRequest = () => ({ type: FETCH_ARTISTS_REQUEST });
export const fetchArtistsSuccess = artists => ({
  type: FETCH_ARTISTS_SUCCESS,
  payload: artists,
});
export const fetchArtistsError = e => ({
  type: FETCH_ARTISTS_ERROR,
  payload: e,
});

// ================ Thunks ================ //

export const fetchArtists = () => (dispatch, getState, sdk) => {
  dispatch(fetchArtistsRequest());

  // Get artists from the global state instead of making an API call
  const state = getState();
  const artists = getArtists(state);
  const isGlobalFetchInProgress = isArtistUsernamesFetchInProgress(state);
  const globalError = getArtistUsernamesError(state);

  // If artists are already available, use them immediately
  if (artists && artists.length > 0) {
    dispatch(fetchArtistsSuccess(artists));
    return Promise.resolve(artists);
  }

  // If there's an error from the global fetch, propagate it
  if (globalError && !isGlobalFetchInProgress) {
    dispatch(fetchArtistsError(globalError));
    return Promise.reject(globalError);
  }

  // If global fetch is in progress, wait for it
  if (isGlobalFetchInProgress) {
    // Return a promise that resolves when the global fetch completes
    return new Promise((resolve, reject) => {
      const checkForData = () => {
        const currentState = getState();
        const currentArtists = getArtists(currentState);
        const currentError = getArtistUsernamesError(currentState);
        const currentInProgress = isArtistUsernamesFetchInProgress(currentState);

        if (currentArtists && currentArtists.length > 0) {
          dispatch(fetchArtistsSuccess(currentArtists));
          resolve(currentArtists);
        } else if (currentError && !currentInProgress) {
          dispatch(fetchArtistsError(currentError));
          reject(currentError);
        } else if (currentInProgress) {
          // Still in progress, check again in a bit
          setTimeout(checkForData, 100);
        } else {
          // No data, no error, not in progress - something went wrong
          const error = storableError(new Error('Failed to fetch artist usernames'));
          dispatch(fetchArtistsError(error));
          reject(error);
        }
      };
      checkForData();
    });
  }

  // If no data and not in progress, trigger a fetch
  return dispatch(fetchArtistUsernames())
    .then(() => {
      const updatedState = getState();
      const fetchedArtists = getArtists(updatedState);
      dispatch(fetchArtistsSuccess(fetchedArtists));
      return fetchedArtists;
    })
    .catch(e => {
      dispatch(fetchArtistsError(storableError(e)));
      throw e;
    });
};

// ================ Page load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // Basic artist data is already loaded on app startup via index.js
  // We only need to fetch the artist images for this page
  return dispatch(fetchArtistsWithListings());
}; 